/**
 * 地址选择页
 * 确认订单 → "去选择地址" → 跳转到此页面
 * 顶部导航栏 + 地址列表 + 底部添加按钮（列表为空时）
 * 点击"添加地址"弹出底部抽屉窗
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchAddressList, deleteAddress, setDefaultAddress } from "@/api/address";
import type { AddressItem } from "@/api/address";
import AddressDrawer from "./components/AddressDrawer";

const PRIMARY = "#FF6B35";

export default function AddressSelectPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // 接收来自确认订单页的 state
  const from = (location.state as Record<string, any>)?.from || "/expert-confirm";
  const staffId = (location.state as Record<string, any>)?.staffId;
  const itemId = (location.state as Record<string, any>)?.itemId;

  // 地址列表
  const [addressList, setAddressList] = useState<AddressItem[]>([]);
  const [loading, setLoading] = useState(true);

  // 抽屉状态
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editAddress, setEditAddress] = useState<AddressItem | null>(null);
  // 从地图选址返回的初始位置（传给 AddressDrawer 回填）
  const [pendingLocation, setPendingLocation] = useState<{
    city: string; township: string; lng: string; lat: string;
  } | null>(null);

  /** 加载地址列表 */
  useEffect(() => {
    fetchAddressList().then((list) => {
      setAddressList(list);
    }).catch((err) => {
      console.error("[地址选择] 加载失败:", err);
      setAddressList([]);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  /** 监听从地图选址页返回的数据，自动打开新增地址抽屉 */
  useEffect(() => {
    const state = location.state as Record<string, any> | null;
    if (state?.selectedCity && state?.source) {
      // 地图选址返回 → 打开抽屉并回填位置
      setPendingLocation({
        city: state.selectedCity,
        township: state.selectedTownship || "",
        lng: state.selectedLng || "",
        lat: state.selectedLat || "",
      });
      setEditAddress(null);
      setDrawerOpen(true);
    }
  }, [location.state]);

  /** 刷新地址列表 */
  const refreshList = async () => {
    try {
      const list = await fetchAddressList();
      setAddressList(list);
    } catch (err) {
      console.error("[地址选择] 刷新失败:", err);
    }
  };

  /** 选择某个地址 → 返回确认订单页 */
  const handleSelectAddress = (item: AddressItem) => {
    navigate(from, {
      replace: true,
      state: {
        ...(location.state as Record<string, any>) || {},
        selectedAddressId: item.id,
        selectedCity: `${item.province}${item.city}${item.district}`,
        selectedDetail: item.detail,
        selectedName: item.real_name,
        selectedPhone: item.phone,
      },
    });
  };

  /** 打开添加地址抽屉 */
  const handleAddAddress = () => {
    setEditAddress(null);
    setDrawerOpen(true);
  };

  /** 打开编辑地址抽屉 */
  const handleEditAddress = (e: React.MouseEvent, item: AddressItem) => {
    e.stopPropagation();
    setEditAddress(item);
    setDrawerOpen(true);
  };

  /** 删除地址 */
  const handleDeleteAddress = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!confirm("确定要删除该地址吗？")) return;

    try {
      await deleteAddress(id);
      await refreshList();
    } catch (err) {
      console.error("[地址选择] 删除失败:", err);
      alert("删除失败，请重试");
    }
  };

  /** 设置默认地址 */
  const handleSetDefault = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await setDefaultAddress(id);
      await refreshList();
    } catch (err) {
      console.error("[地址选择] 设置默认失败:", err);
      alert("设置失败，请重试");
    }
  };

  /** 抽屉关闭回调（保存成功后刷新列表） */
  const handleDrawerClose = (saved?: boolean) => {
    setDrawerOpen(false);
    setEditAddress(null);
    setPendingLocation(null); // 清理地图返回的临时数据
    if (saved) {
      refreshList();
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      {/* ====== 导航栏 ====== */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        display: "flex", alignItems: "center",
        padding: "12px 16px", background: "#fff",
        borderBottom: "1px solid #eee",
      }}>
        <button onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
          style={{ border: "none", padding: 0 }}
        >
          <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#333" }} />
        </button>
        <span style={{ fontSize: 17, fontWeight: 600, color: "#333", marginLeft: 8 }}>地址选择</span>
      </header>

      {/* ====== 内容区域 ====== */}
      <div style={{ padding: "12px 16px", minHeight: "calc(100vh - 52px)" }}>
        {/* loading */}
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
            <div className="w-7 h-7 border-2 border-gray-200 border-t-[#FF6B35] rounded-full animate-spin" />
          </div>
        )}

        {/* 有数据：地址列表 */}
        {!loading && addressList.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {addressList.map((item) => (
              <div key={item.id} onClick={() => handleSelectAddress(item)}
                style={{
                  display: "flex", gap: 14, padding: "16px",
                  borderRadius: 10, background: "#fff",
                  cursor: "pointer",
                }}
              >
                {/* 左侧图标 */}
                <div style={{
                  width: 20, flexShrink: 0, paddingTop: 2,
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%",
                    border: `2px solid ${PRIMARY}`, background: item.is_default ? PRIMARY : "#fff",
                  }} />
                  <div style={{ width: 1, flex: 1, minHeight: 24, background: "#ddd" }} />
                  <i className="ri-map-pin-fill" style={{ fontSize: 14, color: PRIMARY }} />
                </div>

                {/* 右侧内容 */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: "#333" }}>{item.real_name}</span>
                    <span style={{ fontSize: 13, color: "#666" }}>{item.phone}</span>
                    {item.is_default === 1 && (
                      <span style={{
                        fontSize: 10, color: PRIMARY, border: `1px solid ${PRIMARY}`,
                        padding: "1px 5px", borderRadius: 3,
                      }}>默认</span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>
                    {item.province}{item.city}{item.district}{item.detail}
                  </div>
                  {/* 操作按钮 */}
                  <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                    <button
                      onClick={(e) => handleEditAddress(e, item)}
                      style={{
                        fontSize: 12, color: "#666", background: "none", border: "none",
                        cursor: "pointer", padding: 0,
                      }}
                    >
                      <i className="ri-edit-line" /> 编辑
                    </button>
                    {item.is_default !== 1 && (
                      <button
                        onClick={(e) => handleSetDefault(e, item.id)}
                        style={{
                          fontSize: 12, color: "#666", background: "none", border: "none",
                          cursor: "pointer", padding: 0,
                        }}
                      >
                        设为默认
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDeleteAddress(e, item.id)}
                      style={{
                        fontSize: 12, color: "#ff4d4f", background: "none", border: "none",
                        cursor: "pointer", padding: 0,
                      }}
                    >
                      <i className="ri-delete-bin-line" /> 删除
                    </button>
                  </div>
                </div>

                {/* 右侧箭头 */}
                <i className="ri-arrow-right-s-line" style={{ fontSize: 18, color: "#ccc", alignSelf: "center" }} />
              </div>
            ))}
          </div>
        )}

        {/* 无数据：空状态 + 添加按钮 */}
        {!loading && addressList.length === 0 && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", minHeight: 300,
          }}>
            <i className="ri-map-pin-line" style={{ fontSize: 48, color: "#ddd", marginBottom: 12 }} />
            <p style={{ fontSize: 14, color: "#999", marginBottom: 40 }}>暂无收货地址</p>

            <button onClick={handleAddAddress} style={{
              padding: "12px 60px", borderRadius: 25, fontSize: 15, fontWeight: 600,
              color: "#fff", border: "none", cursor: "pointer",
              background: PRIMARY,
            }}>
              + 添加地址
            </button>
          </div>
        )}
      </div>

      {/* 底部固定添加按钮（有数据时显示） */}
      {!loading && addressList.length > 0 && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          padding: "12px 16px", paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
          background: "#fff",
          borderTop: "1px solid #eee", zIndex: 100,
        }}>
          <button onClick={handleAddAddress} style={{
            width: "100%", padding: "14px", borderRadius: 25,
            fontSize: 16, fontWeight: 600, color: "#fff", border: "none",
            cursor: "pointer", background: PRIMARY,
          }}>
            + 新增收货地址
          </button>
        </div>
      )}

      {/* 底部抽屉 - 添加/编辑地址 */}
      <AddressDrawer
        open={drawerOpen}
        address={editAddress}
        initialLocation={pendingLocation}
        onClose={handleDrawerClose}
      />
    </div>
  );
}
